import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  RefreshCw, ChevronLeft, Plus, Settings, CheckCircle, XCircle,
  AlertTriangle, Activity, Send, FileText, Building2, TestTube,
  CreditCard, Globe, Loader2, ArrowUpDown, Eye, Pill, FlaskConical,
  Wallet, Network, Clock
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTEGRATION_TYPES = {
  pharmacy: { label: "Pharmacy", icon: Pill, color: "text-green-400 bg-green-500/20" },
  lab: { label: "Laboratory", icon: FlaskConical, color: "text-blue-400 bg-blue-500/20" },
  payer: { label: "Payer", icon: Wallet, color: "text-amber-400 bg-amber-500/20" },
  hie: { label: "HIE", icon: Network, color: "text-purple-400 bg-purple-500/20" }
};

const STATUS_COLORS = {
  configured: "bg-gray-500/20 text-gray-300",
  connected: "bg-green-500/20 text-green-300",
  error: "bg-red-500/20 text-red-300",
  disabled: "bg-slate-500/20 text-slate-400"
};

export default function InteroperabilityHub() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [pharmacyOrders, setPharmacyOrders] = useState([]);
  const [labOrders, setLabOrders] = useState([]);
  const [claims, setClaims] = useState([]);
  
  // Dialogs
  const [addIntegrationDialog, setAddIntegrationDialog] = useState(false);
  const [newIntegration, setNewIntegration] = useState({
    name: "", type: "pharmacy", vendor: "", protocol: "fhir",
    endpoint_url: "", api_key: ""
  });
  
  const token = localStorage.getItem("hospice_token");

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/interop/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  }, [token]);

  const fetchIntegrations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/interop/integrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIntegrations(data.integrations || []);
      }
    } catch (error) {
      console.error("Error fetching integrations:", error);
    }
  }, [token]);

  const fetchPharmacyOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/interop/pharmacy/orders?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPharmacyOrders(data.orders || []);
      }
    } catch (error) {
      console.error("Error fetching pharmacy orders:", error);
    }
  }, [token]);

  const fetchLabOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/interop/lab/orders?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLabOrders(data.orders || []);
      }
    } catch (error) {
      console.error("Error fetching lab orders:", error);
    }
  }, [token]);

  const fetchClaims = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/interop/claims?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClaims(data.claims || []);
      }
    } catch (error) {
      console.error("Error fetching claims:", error);
    }
  }, [token]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(), 
      fetchIntegrations(),
      fetchPharmacyOrders(),
      fetchLabOrders(),
      fetchClaims()
    ]);
    setLoading(false);
  }, [fetchDashboard, fetchIntegrations, fetchPharmacyOrders, fetchLabOrders, fetchClaims]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  const handleAddIntegration = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/interop/integrations`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(newIntegration)
      });
      
      if (res.ok) {
        toast.success("Integration configured successfully");
        setAddIntegrationDialog(false);
        setNewIntegration({ name: "", type: "pharmacy", vendor: "", protocol: "fhir", endpoint_url: "", api_key: "" });
        fetchIntegrations();
        fetchDashboard();
      } else {
        toast.error("Failed to add integration");
      }
    } catch (error) {
      toast.error("Error adding integration");
    }
  };

  const handleTestIntegration = async (integrationId) => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/interop/integrations/${integrationId}/test`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Connection test passed (${data.response_time_ms}ms)`);
        fetchIntegrations();
      } else {
        toast.error("Connection test failed");
      }
    } catch (error) {
      toast.error("Error testing connection");
    }
  };

  const handleToggleIntegration = async (integrationId, active) => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/interop/integrations/${integrationId}/toggle?active=${active}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(active ? "Integration enabled" : "Integration disabled");
        fetchIntegrations();
        fetchDashboard();
      }
    } catch (error) {
      toast.error("Error toggling integration");
    }
  };

  const handleSyncAll = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(`${API_URL}/api/ehr/interop/sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Synced ${data.integrations_synced} integrations`);
        fetchDashboard();
      }
    } catch (error) {
      toast.error("Error syncing integrations");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const intSummary = dashboard?.integrations || {};

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="interoperability-hub">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => window.location.href = '/hospice-ehr/command-center'}
                className="text-slate-400 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Globe className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Interoperability Hub</h1>
                <p className="text-slate-400 text-sm">Healthcare data exchange integrations</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
                className="border-slate-600 text-slate-300"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                onClick={handleSyncAll}
                disabled={refreshing}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                <ArrowUpDown className="w-4 h-4 mr-2" />
                Sync All
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
          <StatCard 
            icon={<Settings className="w-5 h-5" />} 
            label="Integrations" 
            value={intSummary.total || 0} 
            subtext={`${intSummary.active || 0} active`}
            color="indigo" 
          />
          <StatCard 
            icon={<Pill className="w-5 h-5" />} 
            label="Pharmacy Orders" 
            value={summary.total?.pharmacy_orders || 0}
            subtext={`${summary.today?.pharmacy_orders || 0} today`}
            color="green" 
          />
          <StatCard 
            icon={<FlaskConical className="w-5 h-5" />} 
            label="Lab Orders" 
            value={summary.total?.lab_orders || 0}
            subtext={`${summary.today?.lab_orders || 0} today`}
            color="blue" 
          />
          <StatCard 
            icon={<CreditCard className="w-5 h-5" />} 
            label="Claims" 
            value={summary.total?.claims || 0}
            subtext={`${summary.today?.claims || 0} today`}
            color="amber" 
          />
          <StatCard 
            icon={<FileText className="w-5 h-5" />} 
            label="HIE Documents" 
            value={summary.total?.hie_documents || 0}
            subtext={`${summary.today?.hie_documents || 0} today`}
            color="purple" 
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-800 border border-slate-700 w-full justify-start mb-4">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300">
              <Activity className="w-4 h-4 mr-2" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="integrations" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300">
              <Settings className="w-4 h-4 mr-2" /> Integrations
            </TabsTrigger>
            <TabsTrigger value="pharmacy" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-300">
              <Pill className="w-4 h-4 mr-2" /> Pharmacy
            </TabsTrigger>
            <TabsTrigger value="lab" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300">
              <FlaskConical className="w-4 h-4 mr-2" /> Lab
            </TabsTrigger>
            <TabsTrigger value="claims" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-300">
              <CreditCard className="w-4 h-4 mr-2" /> Claims
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Integration Status */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Network className="w-5 h-5 text-indigo-400" />
                    Integration Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(INTEGRATION_TYPES).map(([key, type]) => {
                      const count = intSummary.by_type?.[key] || 0;
                      const TypeIcon = type.icon;
                      return (
                        <div key={key} className={`flex items-center gap-3 p-3 rounded-lg ${type.color}`}>
                          <TypeIcon className="w-5 h-5" />
                          <div>
                            <p className="text-sm font-medium">{type.label}</p>
                            <p className="text-xs opacity-70">{count} configured</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Button 
                    className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => setAddIntegrationDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Integration
                  </Button>
                </CardContent>
              </Card>

              {/* Today&apos;s Activity */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-cyan-400" />
                    Today&apos;s Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <ActivityRow label="Pharmacy Orders Sent" value={summary.today?.pharmacy_orders || 0} color="green" />
                    <ActivityRow label="Lab Orders Submitted" value={summary.today?.lab_orders || 0} color="blue" />
                    <ActivityRow label="Claims Submitted" value={summary.today?.claims || 0} color="amber" />
                    <ActivityRow label="HIE Documents Exchanged" value={summary.today?.hie_documents || 0} color="purple" />
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <ActionButton icon={<Send />} label="Send e-Prescription" color="green" onClick={() => setActiveTab('pharmacy')} />
                    <ActionButton icon={<TestTube />} label="Order Lab Test" color="blue" onClick={() => setActiveTab('lab')} />
                    <ActionButton icon={<CreditCard />} label="Submit Claim" color="amber" onClick={() => setActiveTab('claims')} />
                    <ActionButton icon={<FileText />} label="Send to HIE" color="purple" onClick={() => toast.info('HIE exchange coming soon')} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-white">Configured Integrations</CardTitle>
                <Button onClick={() => setAddIntegrationDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Integration
                </Button>
              </CardHeader>
              <CardContent>
                {integrations.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No integrations configured</p>
                    <Button className="mt-4 bg-indigo-600" onClick={() => setAddIntegrationDialog(true)}>
                      Add First Integration
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {integrations.map((integration) => {
                      const typeInfo = INTEGRATION_TYPES[integration.type] || INTEGRATION_TYPES.pharmacy;
                      const TypeIcon = typeInfo.icon;
                      return (
                        <div 
                          key={integration.id} 
                          className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg border border-slate-600"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeInfo.color}`}>
                              <TypeIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-medium text-white">{integration.name}</h4>
                              <p className="text-sm text-slate-400">
                                {typeInfo.label} • {integration.protocol.toUpperCase()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={STATUS_COLORS[integration.status] || STATUS_COLORS.configured}>
                              {integration.status}
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTestIntegration(integration.id)}
                              className="border-slate-600"
                            >
                              <TestTube className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant={integration.is_active ? "destructive" : "default"}
                              onClick={() => handleToggleIntegration(integration.id, !integration.is_active)}
                              className={integration.is_active ? "bg-red-600" : "bg-green-600"}
                            >
                              {integration.is_active ? "Disable" : "Enable"}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pharmacy Tab */}
          <TabsContent value="pharmacy">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Pill className="w-5 h-5 text-green-400" />
                  Pharmacy Orders (e-Prescriptions)
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Electronic prescriptions sent to pharmacies
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pharmacyOrders.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Pill className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pharmacy orders yet</p>
                    <p className="text-sm mt-2">Orders will appear here when sent from medication management</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pharmacyOrders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{order.medication_name}</p>
                          <p className="text-sm text-slate-400">
                            {order.patient_name || order.chart_id?.substring(0, 8)} • {order.dosage} • Qty: {order.quantity}
                          </p>
                        </div>
                        <Badge className={
                          order.status === 'filled' ? 'bg-green-500/20 text-green-300' :
                          order.status === 'sent' ? 'bg-blue-500/20 text-blue-300' :
                          order.status === 'error' ? 'bg-red-500/20 text-red-300' :
                          'bg-yellow-500/20 text-yellow-300'
                        }>
                          {order.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lab Tab */}
          <TabsContent value="lab">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-blue-400" />
                  Lab Orders
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Laboratory test orders and results
                </CardDescription>
              </CardHeader>
              <CardContent>
                {labOrders.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No lab orders yet</p>
                    <p className="text-sm mt-2">Lab orders will appear here when submitted</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {labOrders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{order.test_names?.join(', ') || 'Lab Test'}</p>
                          <p className="text-sm text-slate-400">
                            {order.patient_name || order.chart_id?.substring(0, 8)} • {order.lab_name || 'Lab'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={order.priority === 'stat' ? 'bg-red-500/20 text-red-300' : 'bg-slate-500/20 text-slate-300'}>
                            {order.priority}
                          </Badge>
                          <Badge className={
                            order.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                            order.status === 'in_progress' ? 'bg-blue-500/20 text-blue-300' :
                            'bg-yellow-500/20 text-yellow-300'
                          }>
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Claims Tab */}
          <TabsContent value="claims">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-400" />
                  Insurance Claims
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Claims submitted to payers
                </CardDescription>
              </CardHeader>
              <CardContent>
                {claims.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No claims submitted yet</p>
                    <p className="text-sm mt-2">Claims will appear here when created</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {claims.map((claim) => (
                      <div key={claim.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                        <div>
                          <p className="text-white font-medium">
                            {claim.payer_name || 'Payer'} - ${claim.charge_amount?.toFixed(2)}
                          </p>
                          <p className="text-sm text-slate-400">
                            {claim.patient_name || claim.chart_id?.substring(0, 8)} • {claim.service_date}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {claim.paid_amount > 0 && (
                            <span className="text-green-400 text-sm">${claim.paid_amount.toFixed(2)} paid</span>
                          )}
                          <Badge className={
                            claim.status === 'paid' ? 'bg-green-500/20 text-green-300' :
                            claim.status === 'accepted' ? 'bg-blue-500/20 text-blue-300' :
                            claim.status === 'rejected' || claim.status === 'denied' ? 'bg-red-500/20 text-red-300' :
                            'bg-yellow-500/20 text-yellow-300'
                          }>
                            {claim.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Integration Dialog */}
      <Dialog open={addIntegrationDialog} onOpenChange={setAddIntegrationDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Integration</DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure a new external system integration
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Integration Name *</Label>
              <Input 
                value={newIntegration.name}
                onChange={(e) => setNewIntegration({...newIntegration, name: e.target.value})}
                placeholder="e.g., CVS Pharmacy Network"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type *</Label>
                <Select value={newIntegration.type} onValueChange={(v) => setNewIntegration({...newIntegration, type: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {Object.entries(INTEGRATION_TYPES).map(([key, type]) => (
                      <SelectItem key={key} value={key} className="text-white hover:bg-slate-600">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Protocol</Label>
                <Select value={newIntegration.protocol} onValueChange={(v) => setNewIntegration({...newIntegration, protocol: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="fhir" className="text-white">FHIR R4</SelectItem>
                    <SelectItem value="hl7v2" className="text-white">HL7 v2.x</SelectItem>
                    <SelectItem value="ncpdp" className="text-white">NCPDP</SelectItem>
                    <SelectItem value="x12" className="text-white">X12</SelectItem>
                    <SelectItem value="api" className="text-white">REST API</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Vendor</Label>
              <Input 
                value={newIntegration.vendor}
                onChange={(e) => setNewIntegration({...newIntegration, vendor: e.target.value})}
                placeholder="e.g., Surescripts"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label>Endpoint URL</Label>
              <Input 
                value={newIntegration.endpoint_url}
                onChange={(e) => setNewIntegration({...newIntegration, endpoint_url: e.target.value})}
                placeholder="https://api.example.com/v1"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label>API Key</Label>
              <Input 
                type="password"
                value={newIntegration.api_key}
                onChange={(e) => setNewIntegration({...newIntegration, api_key: e.target.value})}
                placeholder="Enter API key"
                className="bg-slate-700 border-slate-600"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddIntegrationDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleAddIntegration} className="bg-indigo-600 hover:bg-indigo-700">
              Add Integration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, subtext, color }) {
  const colorClasses = {
    indigo: "from-indigo-500/20 to-indigo-600/10 border-indigo-500/30 text-indigo-400",
    green: "from-green-500/20 to-green-600/10 border-green-500/30 text-green-400",
    blue: "from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400",
    amber: "from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400",
    purple: "from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400"
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-3xl font-bold">{value}</div>
      {subtext && <div className="text-xs text-slate-400 mt-1">{subtext}</div>}
    </div>
  );
}

function ActivityRow({ label, value, color }) {
  const colors = {
    green: "bg-green-500",
    blue: "bg-blue-500",
    amber: "bg-amber-500",
    purple: "bg-purple-500"
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${colors[color]}`} />
        <span className="text-slate-300 text-sm">{label}</span>
      </div>
      <span className="text-white font-medium">{value}</span>
    </div>
  );
}

function ActionButton({ icon, label, color, onClick }) {
  const colors = {
    green: "bg-green-600 hover:bg-green-700",
    blue: "bg-blue-600 hover:bg-blue-700",
    amber: "bg-amber-600 hover:bg-amber-700",
    purple: "bg-purple-600 hover:bg-purple-700"
  };

  return (
    <Button className={`${colors[color]} flex items-center justify-center gap-2 h-12`} onClick={onClick}>
      {icon}
      <span className="text-sm">{label}</span>
    </Button>
  );
}
